import { SIGNATURE_DEFAULTS } from '@client/enums';

const HEX_COLOR_REGEXP = /^#(?:[\da-f]{3}|[\da-f]{6})$/i;

const escapeHtmlAttribute = (value) =>
  value
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

/**
 * Strips a leading '#' from a hex color (icon-service params expect the hash-less form).
 * @param {string} hex
 * @returns {string}
 */
export const stripHash = (hex) => (hex || '').replace(/^#/, '');

/**
 * Resolves the provider photo-path segment from the per-domain provider flags.
 * Falls back to 'office365' so the photo path is never 'undefined.png'.
 * @param {{ isMicrosoft?: boolean, isGoogle?: boolean, isBambooHR?: boolean }} flags
 * @returns {'office365' | 'google' | 'bamboohr'}
 */
export const resolveSignaturePlatform = ({ isMicrosoft, isGoogle, isBambooHR } = {}) =>
  isMicrosoft ? 'office365' : isGoogle ? 'google' : isBambooHR ? 'bamboohr' : 'office365';

/**
 * Builds the brand tokens baked into each template's source at creation time.
 * @param {{ colorPrimary?: string, colorText?: string, logoUrl?: string }} defaults
 * @returns {{ logoUrl: string, primary: string, primaryNoHash: string, text: string }}
 */
export const buildBrandTokens = (defaults = {}) => {
  const primary = HEX_COLOR_REGEXP.test(defaults.colorPrimary || '') ? defaults.colorPrimary : '#0087ff';
  const text = HEX_COLOR_REGEXP.test(defaults.colorText || '') ? defaults.colorText : '#111111';
  // Only bake a real http(s) brand logo. An unbranded tenant's getTemplateDefaults()
  // returns a "YOUR LOGO HERE" data: URI placeholder (stores/bizcard.js), which is always
  // truthy — treat any non-http value as absent so we fall back to the real logo and never
  // (a) ship the placeholder to production, nor (b) let fixImagesInline corrupt a data: URI
  // by appending ?inline=1.
  let logoUrl = SIGNATURE_DEFAULTS.logo;
  const logoUrlCandidate = typeof defaults.logoUrl === 'string' ? defaults.logoUrl.trim() : '';

  try {
    const parsedLogoUrl = new URL(logoUrlCandidate);

    if (['http:', 'https:'].includes(parsedLogoUrl.protocol)) {
      logoUrl = logoUrlCandidate;
    }
  } catch {
    // Keep the known-safe default when the supplied logo is not a valid absolute URL.
  }

  return {
    logoUrl: escapeHtmlAttribute(logoUrl),
    primary,
    primaryNoHash: stripHash(primary),
    text
  };
};

/**
 * Emits the shared <style> link-reset block plus the [if mso] link-reset block that
 * prefixes every template's source. Returns a raw string (no trailing newline); both
 * color slots interpolate `text`.
 * @param {{ text: string }} params
 * @returns {string}
 */
export const buildLinkResetHeader = ({ text }) => `<style type="text/css">
  a, a:link, a:visited, a:hover, a:active,
  a[x-apple-data-detectors],
  a[x-apple-data-detectors] *,
  .x-gmail-data-detectors,
  .x-gmail-data-detectors *,
  .aBn,
  #MessageViewBody a,
  u + #body a {
         color: ${text} !important;
      text-decoration: none !important;
      border-bottom: none !important;
  }
</style>

<!--[if mso]>
  <style type="text/css">
      a {
          color: ${text} !important;
          text-decoration: none !important;
          border-bottom: none !important;
      }
  </style>
<![endif]-->`;

/**
 * Emits one social-icon `<td>` cell (guarded by `{% if <field> %}`). Returns a raw string
 * with full leading indentation on every line (the `<td>` line sits at `indent` spaces, the
 * `{% if %}`/`{% endif %}` guards at `indent - 2`); the call site interpolates it at column 0.
 * Every near-identical difference is an explicit param: `field` is the Liquid guard + href key,
 * `type`/`alt` come from the plan's icon table (twitter → type='x', alt='twitter'), and the
 * icon-service colors are `background`/`color`/`radius` (filled: background=<primaryNoHash>,
 * color='ffffff', radius=100 · outline: background='none', color=<primaryNoHash>, radius=4).
 * @param {{ indent: number, field: string, type: string, alt: string, background: string, color: string, radius: number, size?: number }} params
 * @returns {string}
 */
export const buildSocialIconTd = ({ indent, field, type, alt, background, color, radius, size = 24 }) => {
  const base = ' '.repeat(indent);
  const guard = ' '.repeat(indent - 2);
  return `${guard}{% if ${field} %}
${base}<td style="padding-right: 4px;">
${base}  <a href="{{${field}}}" target="_blank" style="text-decoration: none;display: inline-block;">
${base}    <img
${base}      src="{{sgcnameproto}}://{{sgcname}}/v3/icons?type=${type}&background=${background}&color=${color}&size=${size}&radius=${radius}"
${base}      alt="${alt}"
${base}      style="width: ${size}px;height: ${size}px;display: block;border: 0;outline: none;"
${base}      width="${size}"
${base}      height="${size}"
${base}    >
${base}  </a>
${base}</td>
${guard}{% endif %}`;
};

/**
 * Emits the VML + <img> headshot sequence (MSO `<v:oval>`/`<v:fill>` fallback plus the
 * non-MSO `<img>`) shared by T3/T4/T5. Returns a raw string with full leading indentation
 * on every line (base at `indent`). The outer `<td>`/`<tr>` skeleton stays explicit per
 * template. `strokeweight` derives from `borderWidth`; a non-zero `borderWidth` also adds a
 * `border: <n>px solid <primary>;` segment to the img style (omitted when `borderWidth` is 0).
 * @param {{ indent: number, size: number, borderWidth: number, ovalStyleDims: string, primary: string }} params
 * @returns {string}
 */
export const buildHeadshotBlock = ({ indent, size, borderWidth, ovalStyleDims, primary }) => {
  const b = ' '.repeat(indent);
  const border = borderWidth ? `border: ${borderWidth}px solid ${primary};` : '';
  return `${b}<!--[if (gte mso 9)|(IE)]>
${b}  <v:oval xmlns:v="urn:schemas-microsoft-com:vml" style="${ovalStyleDims}" fill="t" strokecolor="${primary}" strokeweight="${borderWidth}px">
${b}    <v:fill type="frame" src="{{sgcnameproto}}://{{sgcname}}/photo/{{sgsender}}/__PLATFORM__.png?shape=circle" />
${b}  </v:oval>
${b}<![endif]-->
${b}<!-- [if !mso]> -->
${b}<img
${b}  src="{{sgcnameproto}}://{{sgcname}}/photo/{{sgsender}}/__PLATFORM__.png?shape=circle"
${b}  alt="Headshot"
${b}  style="width: ${size}px;height: ${size}px;display: block;border: 0;outline: none;${border}border-radius: 50%;"
${b}  width="${size}"
${b}  height="${size}"
${b}>
${b}<!-- <![endif] -->`;
};

/**
 * Emits one T4 CTA button cell (`<td>…<a>…</a></td>`). Returns a raw string with full leading
 * indentation on every line (the `<td>` sits at `indent`). The surrounding row/table layout
 * and spacer cells stay explicit in the template. `primary` is interpolated into both the
 * `bgcolor` attribute and the inline `background-color`. `hrefToken` is the Liquid variable
 * rendered as `href="{{ <token> }}"`.
 * @param {{ indent: number, width: number, hrefToken: string, label: string, fontSize: string, primary: string }} params
 * @returns {string}
 */
export const buildCtaButton = ({ indent, width, hrefToken, label, fontSize, primary }) => {
  const b = ' '.repeat(indent);
  return `${b}<td
${b}  width="${width}"
${b}  height="27"
${b}  align="center"
${b}  valign="middle"
${b}  bgcolor="${primary}"
${b}  style="background-color:${primary}; width:${width}px; height:27px;"
${b}>
${b}  <a
${b}    href="{{ ${hrefToken} }}"
${b}    target="_blank"
${b}    style="display:block; width:${width}px; line-height:27px; font-size:${fontSize}; font-family: Helvetica, Arial, sans-serif; color: #ffffff !important; text-decoration: none !important; text-align:center;"
${b}  >
${b}    ${label}
${b}  </a>
${b}</td>`;
};
